import {
  REQUIRED_MESSAGE,
  dimensionProblem,
  text,
  urlProblem,
  when,
} from "@/lib/catalog-item-form-rules";

// The Request Item dialog's model: what the typist has entered, what is wrong
// with it, and what that becomes on the wire.
//
// It is a plain module rather than part of the component because all three are
// decisions the dialog only *displays* — the donor keeps them in react-hook-form
// `rules` and a `buildPostData` helper (`RequestItemIsland.tsx`,
// `useRequestItemForm.ts`), and the shapes of both port here intact.
//
// **The browser's validation is the donor's, not Python's.** Python
// (`app/use_cases/catalog/item_patch.py`) is the enforcement and refuses more
// than this does; these rules exist so a typist hears about a 51-character model
// number without a round trip, because a `.validator` rejection is scrubbed to a
// generic message by the global error middleware (`CLAUDE.md`, placement 8).
//
// Two fields are **normalised on the way out** rather than refused, because the
// donor accepts a shape Python's column format does not — see `createInputFromDraft`.
//
// What this form shares with the Add Variant dialog is in
// `@/lib/catalog-item-form-rules` — the required message, the URL rule, the
// dimension floor, and the populate-only-if-truthy payload shape. The donor
// shares those four too (one `validateDimensionValue`, one `urlValidation`);
// everything still here is this form's alone, and several fields differ between
// the two dialogs on purpose.

/** The donor's cap on the model number field (`RequestItemIsland.tsx`). It is
 * the form's, not the column's: `model_number` is 255 wide in Postgres, and
 * Python carries the 50 for exactly this reason (`_TEXT_LIMITS`). */
export const MODEL_NUMBER_MAX_LENGTH = 50;

/** The prop-id column's width, and the donor's `PROP_ID_MAX_LENGTH`. */
export const PROP_ID_MAX_LENGTH = 64;

/**
 * One Prop ID or several separated by commas, whitespace around them tolerated.
 *
 * Verbatim from the donor (`timmy/src/utils/validation.ts`): shower and
 * tub-shower faucets carry more than one, so they are stored in one string
 * field, and a typist mid-edit or pasting from a spreadsheet must not be refused
 * over a stray space.
 *
 * Python accepts the same shape and stores it as typed (`item_patch.py`'s
 * `_PROP_ID`), so nothing is rewritten on the way to the wire and nothing is
 * accepted here that the save then refuses.
 */
export const PROP_ID_PATTERN = /^\d+(\s*,\s*\d+)*$/;

/**
 * The dialog's fields, one per control.
 *
 * The four selects hold an id or `null`; every text field holds a string, blank
 * when untouched — which is what a text input gives back, and keeping it means
 * "empty" has one spelling instead of two.
 */
/**
 * The draft as the create endpoint takes it.
 *
 * **Declared here, not imported from `@/api/contracts/catalog`.** `lib/` sits
 * *below* both graphs and may not import upward (`client/docs/architecture.md`,
 * "The dependency rule"), and this module is `lib/` because the dialog and its
 * tests both name it. The two shapes are held together by a drift test in the
 * `api/` tier — the one tier that can see both sides — exactly as
 * `CatalogListItem` is (`api/contracts/catalog-wire-drift.unit.test.ts`).
 */
export interface RequestItemPayload {
  name: string;
  manufacturerId: number;
  categoryId: number;
  finishId: number;
  materialId?: number;
  modelNumber?: string;
  jdeItemNumber?: string;
  propId?: string;
  url?: string;
  description?: string;
  dimensionWidth?: string;
  dimensionHeight?: string;
  dimensionDepth?: string;
  imageIds?: number[];
}

export interface RequestItemDraft {
  name: string;
  manufacturerId: number | null;
  categoryId: number | null;
  finishId: number | null;
  materialId: number | null;
  modelNumber: string;
  jdeItemNumber: string;
  propId: string;
  dimensionWidth: string;
  dimensionHeight: string;
  dimensionDepth: string;
  url: string;
  description: string;
}

export type RequestItemFieldProblems = Partial<Record<keyof RequestItemDraft, string>>;

export const blankRequestItemDraft = (): RequestItemDraft => ({
  name: "",
  manufacturerId: null,
  categoryId: null,
  finishId: null,
  materialId: null,
  modelNumber: "",
  jdeItemNumber: "",
  propId: "",
  dimensionWidth: "",
  dimensionHeight: "",
  dimensionDepth: "",
  url: "",
  description: "",
});

/** Every problem the browser can see, keyed by field. An empty object is a
 * submittable draft. */
export function requestItemFieldProblems(draft: RequestItemDraft): RequestItemFieldProblems {
  const problems: RequestItemFieldProblems = {};

  // Bare `required`, matching the donor's rule (`RequestItemIsland.tsx:100`)
  // rather than tightening it: react-hook-form counts `''` as missing and lets a
  // name of spaces through. The Add Variant form's identical rule reads the same
  // way, which is the point — the two used to disagree here.
  if (!draft.name) problems.name = REQUIRED_MESSAGE;
  if (draft.manufacturerId === null) problems.manufacturerId = REQUIRED_MESSAGE;
  if (draft.categoryId === null) problems.categoryId = REQUIRED_MESSAGE;
  if (draft.finishId === null) problems.finishId = REQUIRED_MESSAGE;

  if (draft.modelNumber.length > MODEL_NUMBER_MAX_LENGTH) {
    problems.modelNumber = `Maximum length is ${MODEL_NUMBER_MAX_LENGTH} characters`;
  }

  if (draft.propId.trim()) {
    // Matched on the trimmed value, which is both what the donor's own regex
    // tolerates (`^\s*\d+…\s*$`) and what Python compares — `_text` trims
    // before `_PROP_ID` sees it. The length cap is the raw field's, because
    // that is the string the column would have to hold.
    if (draft.propId.length > PROP_ID_MAX_LENGTH) {
      problems.propId = `Maximum length is ${PROP_ID_MAX_LENGTH} characters`;
    } else if (!PROP_ID_PATTERN.test(draft.propId.trim())) {
      problems.propId = "Enter one Prop ID or several separated by commas (digits only)";
    }
  }

  const url = urlProblem(draft.url);
  if (url) problems.url = url;

  for (const field of ["dimensionWidth", "dimensionHeight", "dimensionDepth"] as const) {
    const problem = dimensionProblem(draft[field]);
    if (problem) problems[field] = problem;
  }

  return problems;
}

/**
 * The draft as the create endpoint's input — the donor's `buildPostData`.
 *
 * **A blank optional field is left out entirely, not sent as `null`.** The
 * create wire's optional keys are absent-or-value, and an item requested with
 * nine explicit nulls would say more than the typist did.
 */
export function createInputFromDraft(
  draft: RequestItemDraft,
  imageIds: readonly number[] = [],
): RequestItemPayload {
  if (draft.manufacturerId === null || draft.categoryId === null || draft.finishId === null) {
    // Unreachable from the dialog, which cannot submit with a problem
    // outstanding. It states the rule rather than casting it away, so a future
    // caller that skips the check is told why.
    throw new Error("A requested item needs a manufacturer, a category and a color/finish.");
  }

  const propId = text(draft.propId);
  const url = text(draft.url);

  return {
    // As typed: the donor posts `data.name` untouched.
    name: draft.name,
    manufacturerId: draft.manufacturerId,
    categoryId: draft.categoryId,
    finishId: draft.finishId,
    ...(draft.materialId === null ? {} : { materialId: draft.materialId }),
    ...when("modelNumber", text(draft.modelNumber)),
    ...when("jdeItemNumber", text(draft.jdeItemNumber)),
    ...when("propId", propId),
    ...when("dimensionWidth", text(draft.dimensionWidth)),
    ...when("dimensionHeight", text(draft.dimensionHeight)),
    ...when("dimensionDepth", text(draft.dimensionDepth)),
    ...when("url", url),
    ...when("description", text(draft.description)),
    // Absent when nothing was attached, for the same reason every other blank
    // field is absent: an empty list would say something the typist did not.
    ...(imageIds.length === 0 ? {} : { imageIds: [...imageIds] }),
  };
}
